import React, { useEffect, useMemo } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { StackedHeader } from 'components/DataDisplay';
import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import { RepairIcon, VulnerabilityIcon } from 'components/Icons';
import { Tabs } from 'components/Navigation';
import { Drawer } from 'components/Overlay';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { selectSharedState } from 'containers/SharedState/selectors';
import { ContainerImages } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ContainerImages';
import { useResourceVulnerabilities } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/hooks/useResourceVulnerability';
import { ResourceDetails } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceDetails';
import { getResourceHeaderLabels } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/resourceHeaderUtils';
import { ResourceVulnerabilityActions } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilityActions';
import { ResourceVulnerabilityDetailsTable } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilityDetailsTable';
import { ResourceVulnerabilityFixPlanTable } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilityFixPlanTable';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';

export interface ResourceVulnerabilityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  uuid: string;
}

export const ResourceVulnerabilityDrawer: React.FC<ResourceVulnerabilityDrawerProps> = props => {
  const { isOpen, onClose, uuid } = props;
  const { getResourceVulnerabilities } = useResourceVulnerabilities({ uuid });
  const { resourceInsight } = useSelector(selectVulnerabilities);
  const [tabIndex, setTabIndex] = React.useState(0);
  const { isDevOrTest } = useSelector(selectSharedState);

  const isContainer = useMemo(
    () =>
      [
        NativeResources.Container,
        NativeResources.GcpContainer,
        NativeResources.AzureContainer,
      ].includes(resourceInsight.data?.native_name as any),
    [resourceInsight.data?.native_name],
  );

  useEffect(() => {
    if (tabIndex === 0) getResourceVulnerabilities();
  }, [getResourceVulnerabilities, tabIndex]);

  const tabs = [
    {
      title: 'CVEs',
      key: 'cve',
      icon: <VulnerabilityIcon />,
      component: (
        <ResourceVulnerabilityDetailsTable isContainer={isContainer} />
      ),
    },
    {
      title: 'Fix Plan',
      key: 'fix_plan',
      icon: <RepairIcon />,
      component: <ResourceVulnerabilityFixPlanTable />,
    },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          // Missing name/type parts are skipped rather than shown as "undefined".
          upper={getResourceHeaderLabels(resourceInsight.data).upper}
          lower={getResourceHeaderLabels(resourceInsight.data).lower}
          type={resourceInsight.data?.native_name}
          isLoading={resourceInsight.isLoading}
        />
      }
      body={
        <ActiveResourceProvider>
          <WithSpinner
            loadStatus={{ loading: resourceInsight.isLoading }}
            spinnerSize="lg"
          >
            <Stack w="full" h="full" spacing={5}>
              <ResourceVulnerabilityActions />
              <ResourceDetails uuid={uuid} />
              {resourceInsight.data?.native_name ===
              NativeResources.ElasticContainerRegistry ? (
                <ContainerImages />
              ) : resourceInsight.data?.native_name ===
                  NativeResources.Ec2Instance || isDevOrTest ? (
                <Tabs items={tabs} styles={{ tabPanel: { pt: 4 } }} />
              ) : (
                <ResourceVulnerabilityDetailsTable isContainer={isContainer} />
              )}
            </Stack>
          </WithSpinner>
        </ActiveResourceProvider>
      }
      styles={{
        content: { maxW: '1650px' },
        body: { overflowY: 'auto' },
        drawer: {
          //portalProps: { containerRef: ref },
          blockScrollOnMount: false,
          trapFocus: false,
        },
      }}
    />
  );
};
